import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { AdminPageHeader, Field, Select, ConfirmDeleteButton } from "./_helpers";
import { toast } from "sonner";
import { Download, RefreshCw, Globe, Info, Clock, Play, CheckCircle2, XCircle } from "lucide-react";

export default function AdminPharmacies() {
  const [items, setItems] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [logs, setLogs] = useState([]);
  const empty = { name: "", province_id: "", district: "", address: "", phone: "", on_duty_date: new Date().toISOString().slice(0, 10), on_duty_hours: "19:00 - 09:00", is_active: true };
  const [f, setF] = useState(empty);
  const [provFilter, setProvFilter] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [tab, setTab] = useState("manual");
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [schedulerSaving, setSchedulerSaving] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  const [schedulerRuns, setSchedulerRuns] = useState([]);

  const load = () => api.get(`/pharmacies${provFilter ? `?province_id=${provFilter}` : ""}`).then((r) => setItems(r.data));
  const loadLogs = () => api.get("/pharmacies/scraper-logs?limit=20").then((r) => setLogs(r.data));
  const loadScheduler = () => {
    api.get("/pharmacies/scheduler-status").then((r) => setSchedulerStatus(r.data)).catch(() => {});
    api.get("/pharmacies/scheduler-runs?limit=10").then((r) => setSchedulerRuns(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [provFilter]);
  useEffect(() => {
    api.get("/provinces?active_only=false").then((r) => setProvinces(r.data));
    loadLogs();
    loadScheduler();
  }, []);

  const save = async () => {
    try { await api.post("/pharmacies", f); setF(empty); load(); toast.success("Eklendi"); } catch { toast.error("Hata"); }
  };

  const syncOne = async () => {
    if (!provFilter) return toast.error("Önce bir il seçin");
    setSyncing(true);
    try {
      const r = await api.post(`/pharmacies/sync/${provFilter}`);
      if (r.data.ok) {
        toast.success(`${r.data.count} eczane çekildi (${r.data.dates.join(", ")})`);
        load(); loadLogs();
      } else {
        toast.error(r.data.error || "Çekilemedi");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
    finally { setSyncing(false); }
  };

  const syncAll = async () => {
    if (!window.confirm("Tüm aktif iller için eczane verisi çekilecek. Bu işlem birkaç dakika sürebilir. Devam edilsin mi?")) return;
    setSyncingAll(true);
    try {
      const r = await api.post("/pharmacies/sync-all");
      toast.success(`Tamamlandı: ${r.data.ok_count} başarılı, ${r.data.fail_count} başarısız (${r.data.total} il)`);
      load(); loadLogs();
    } catch (e) { toast.error("Hata"); }
    finally { setSyncingAll(false); }
  };

  const saveScheduler = async (patch) => {
    setSchedulerSaving(true);
    try {
      const r = await api.put("/pharmacies/scheduler-config", patch);
      setSchedulerStatus(r.data.status);
      toast.success("Zamanlayıcı güncellendi");
    } catch { toast.error("Kayıt başarısız"); }
    finally { setSchedulerSaving(false); }
  };

  const runNow = async () => {
    setRunningNow(true);
    try {
      const r = await api.post("/pharmacies/scheduler-run-now");
      toast.success(`Çalıştırıldı: ${r.data.ok_count} başarılı, ${r.data.fail_count} başarısız (${r.data.total} il)`);
      load(); loadLogs(); loadScheduler();
    } catch { toast.error("Çalıştırma başarısız"); }
    finally { setRunningNow(false); }
  };

  const formatRunTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" });
    } catch { return iso; }
  };

  return (
    <div>
      <AdminPageHeader title="Nöbetçi Eczaneler" subtitle="3 mod: Manuel giriş • Otomatik çekim (eczaneler.gen.tr) • API entegrasyonu (Ayarlar'dan)" />

      <div className="flex gap-2 mb-4 flex-wrap">
        <button onClick={() => setTab("manual")} className={`px-3 py-1.5 rounded text-sm ${tab === "manual" ? "bg-[#e11d2a] text-white" : "border"}`}>Manuel Giriş</button>
        <button onClick={() => setTab("scraper")} className={`px-3 py-1.5 rounded text-sm flex items-center gap-1 ${tab === "scraper" ? "bg-[#e11d2a] text-white" : "border"}`}><Globe className="w-4 h-4" /> Otomatik Çek</button>
        <button onClick={() => setTab("scheduler")} className={`px-3 py-1.5 rounded text-sm flex items-center gap-1 ${tab === "scheduler" ? "bg-[#e11d2a] text-white" : "border"}`}><Clock className="w-4 h-4" /> Zamanlayıcı</button>
        <button onClick={() => setTab("logs")} className={`px-3 py-1.5 rounded text-sm ${tab === "logs" ? "bg-[#e11d2a] text-white" : "border"}`}>Çekim Logları</button>
      </div>

      <div className="mb-4 max-w-sm">
        <Select label="İl filtre" value={provFilter} options={[{ value: "", label: "Tümü" }, ...provinces.map((p) => ({ value: p.id, label: p.name }))]} onChange={(e) => setProvFilter(e.target.value)} />
      </div>

      {tab === "scraper" && (
        <div className="bg-white border border-gray-100 rounded-lg p-5 mb-4">
          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900 flex gap-2 mb-4">
            <Info className="w-5 h-5 shrink-0" />
            <div>
              <strong>Otomatik Çekim:</strong> <code className="bg-white px-1 rounded">eczaneler.gen.tr/nobetci-{`{il-slug}`}</code> sitesinden bugün, yarın ve öbür gün için nöbetçi eczaneler otomatik çekilir.
              <br />Daha sonra <strong>günde her 6 saatte bir</strong> otomatik güncellenir (Ayarlar → Entegrasyonlar → Pharmacy: Mode = <code className="bg-white px-1 rounded">scraper</code> olduğunda).
              <br />API anahtarına gerek yoktur. Çekilen veriler kaynak işaretiyle (<code className="bg-white px-1 rounded">source: eczaneler.gen.tr</code>) kaydedilir; manuel girdiklerinizle karışmaz.
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button disabled={syncing || !provFilter} onClick={syncOne} className="px-4 py-2 bg-[#e11d2a] text-white rounded text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
              <Download className="w-4 h-4" /> {syncing ? "Çekiliyor..." : "Seçili İl İçin Şimdi Çek"}
            </button>
            <button disabled={syncingAll} onClick={syncAll} className="px-4 py-2 bg-[#0b0b0d] text-white rounded text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
              <RefreshCw className={`w-4 h-4 ${syncingAll ? "animate-spin" : ""}`} /> {syncingAll ? "Tüm İller İşleniyor..." : "Tüm Aktif İller İçin Çek"}
            </button>
          </div>
        </div>
      )}

      {tab === "scheduler" && schedulerStatus && (
        <div className="bg-white border border-gray-100 rounded-lg p-5 mb-4 space-y-4" data-testid="admin-scheduler-block">
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900 flex gap-2">
            <Clock className="w-5 h-5 shrink-0" />
            <div>
              <strong>Günlük Otomatik Çekim:</strong> Belirlenen saatte (varsayılan <code className="bg-white px-1 rounded">08:00 Europe/Istanbul</code>) tüm aktif iller için
              eczane verisi otomatik çekilir. Sunucu yeniden başlasa bile zamanlayıcı otomatik kurulur.
            </div>
          </div>

          {/* Status row */}
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="border rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wide text-gray-500 font-bold">Durum</div>
              <div className="mt-1 font-semibold text-sm flex items-center gap-1">
                {schedulerStatus.config?.enabled ? (
                  <><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Aktif</>
                ) : (
                  <><XCircle className="w-4 h-4 text-gray-400" /> Devre dışı</>
                )}
              </div>
            </div>
            <div className="border rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wide text-gray-500 font-bold">Sonraki Çalışma</div>
              <div className="mt-1 font-semibold text-sm" data-testid="scheduler-next-run">
                {schedulerStatus.job?.scheduled ? formatRunTime(schedulerStatus.job.next_run_time) : "Planlanmadı"}
              </div>
            </div>
            <div className="border rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wide text-gray-500 font-bold">Son Çalışma</div>
              <div className="mt-1 font-semibold text-sm">
                {schedulerStatus.last_run
                  ? `${formatRunTime(schedulerStatus.last_run.ran_at)} • ${schedulerStatus.last_run.ok_count}/${schedulerStatus.last_run.total} OK`
                  : "Henüz çalışmadı"}
              </div>
            </div>
          </div>

          {/* Config row */}
          <div className="border rounded-lg p-4 space-y-3">
            <div className="font-bold text-sm">Yapılandırma</div>
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={!!schedulerStatus.config?.enabled}
                onChange={(e) => saveScheduler({ enabled: e.target.checked })}
                disabled={schedulerSaving}
                className="w-4 h-4 accent-[#e11d2a]"
                data-testid="scheduler-enabled-toggle"
              />
              <span>Otomatik çekim aktif</span>
            </label>
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <div className="text-xs text-gray-600 mb-1">Saat</div>
                <input
                  type="number" min="0" max="23"
                  value={schedulerStatus.config?.hour ?? 8}
                  onChange={(e) => setSchedulerStatus({ ...schedulerStatus, config: { ...schedulerStatus.config, hour: parseInt(e.target.value || "0", 10) } })}
                  className="w-20 px-2 py-1.5 border rounded text-sm"
                  data-testid="scheduler-hour-input"
                />
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">Dakika</div>
                <input
                  type="number" min="0" max="59"
                  value={schedulerStatus.config?.minute ?? 0}
                  onChange={(e) => setSchedulerStatus({ ...schedulerStatus, config: { ...schedulerStatus.config, minute: parseInt(e.target.value || "0", 10) } })}
                  className="w-20 px-2 py-1.5 border rounded text-sm"
                  data-testid="scheduler-minute-input"
                />
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">Zaman Dilimi</div>
                <input
                  type="text"
                  value={schedulerStatus.config?.timezone || "Europe/Istanbul"}
                  onChange={(e) => setSchedulerStatus({ ...schedulerStatus, config: { ...schedulerStatus.config, timezone: e.target.value } })}
                  className="w-44 px-2 py-1.5 border rounded text-sm"
                />
              </div>
              <button
                disabled={schedulerSaving}
                onClick={() => saveScheduler({
                  hour: schedulerStatus.config.hour,
                  minute: schedulerStatus.config.minute,
                  timezone: schedulerStatus.config.timezone,
                })}
                data-testid="scheduler-save-btn"
                className="px-4 py-2 bg-[#0b0b0d] text-white rounded text-sm font-semibold disabled:opacity-50"
              >
                {schedulerSaving ? "Kaydediliyor..." : "Kaydet"}
              </button>
              <button
                disabled={runningNow}
                onClick={runNow}
                data-testid="scheduler-run-now-btn"
                className="px-4 py-2 bg-[#e11d2a] text-white rounded text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
              >
                <Play className="w-4 h-4" /> {runningNow ? "Çalışıyor..." : "Şimdi Çalıştır"}
              </button>
            </div>
          </div>

          {/* Last runs table */}
          <div>
            <div className="font-bold text-sm mb-2">Son Çalışmalar</div>
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr><th className="px-3 py-2">Tarih</th><th className="px-3 py-2">Tetik</th><th className="px-3 py-2">Süre</th><th className="px-3 py-2">İl</th><th className="px-3 py-2">Durum</th></tr>
                </thead>
                <tbody>
                  {schedulerRuns.map((r) => (
                    <tr key={r.id} className="border-t">
                      <td className="px-3 py-2 text-xs">{formatRunTime(r.ran_at)}</td>
                      <td className="px-3 py-2 text-xs"><span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">{r.trigger}</span></td>
                      <td className="px-3 py-2 text-xs">{r.duration_sec ? `${r.duration_sec.toFixed(1)}s` : "—"}</td>
                      <td className="px-3 py-2 text-xs font-bold">{r.ok_count}/{r.total}</td>
                      <td className="px-3 py-2 text-xs">
                        <span className={`px-2 py-0.5 rounded ${r.status === "ok" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{r.status}</span>
                      </td>
                    </tr>
                  ))}
                  {schedulerRuns.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-gray-500">Henüz çalıştırılmadı</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === "logs" && (
        <div className="bg-white border border-gray-100 rounded-lg overflow-x-auto mb-4">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left"><tr><th className="px-3 py-2">Tarih</th><th className="px-3 py-2">İl</th><th className="px-3 py-2">Adet</th><th className="px-3 py-2">Tarihler</th><th className="px-3 py-2">Durum</th></tr></thead>
            <tbody>
              {logs.map((l) => {
                const provName = provinces.find((p) => p.id === l.province_id)?.name || "—";
                return (
                  <tr key={l.id} className="border-t">
                    <td className="px-3 py-2 text-xs">{new Date(l.ran_at).toLocaleString("tr-TR")}</td>
                    <td className="px-3 py-2 text-xs">{provName}</td>
                    <td className="px-3 py-2 text-xs font-bold">{l.count}</td>
                    <td className="px-3 py-2 text-xs">{(l.dates || []).join(", ")}</td>
                    <td className="px-3 py-2 text-xs"><span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">{l.status}</span></td>
                  </tr>
                );
              })}
              {logs.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-gray-500">Henüz otomatik çekim yapılmadı</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-100 rounded-lg p-4 max-h-[600px] overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-sm">Mevcut Eczaneler ({items.length})</h3>
            <button onClick={load} className="text-xs text-blue-600 inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Yenile</button>
          </div>
          {items.map((p) => (
            <div key={p.id} className="py-2 border-b flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-semibold text-sm truncate">{p.name}</div>
                  {p.source === "eczaneler.gen.tr" && <span className="shrink-0 text-[9px] uppercase px-1 py-0.5 bg-blue-100 text-blue-700 rounded font-bold">Otomatik</span>}
                </div>
                <div className="text-xs text-gray-500">{p.district || "Merkez"} • {p.phone} • {p.on_duty_date}</div>
                {p.address && <div className="text-[10px] text-gray-400 truncate">{p.address}</div>}
              </div>
              <ConfirmDeleteButton onConfirm={async () => { await api.delete(`/pharmacies/${p.id}`); load(); }} />
            </div>
          ))}
          {items.length === 0 && <div className="text-sm text-gray-500 text-center p-6">Hiç eczane yok. Manuel ekleyin ya da Otomatik Çek butonunu kullanın.</div>}
        </div>

        {tab === "manual" && (
          <div className="bg-white border border-gray-100 rounded-lg p-4 space-y-2 h-fit">
            <h3 className="font-bold">Yeni Eczane (Manuel)</h3>
            <Field label="Ad" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
            <Select label="İl" value={f.province_id} options={[{ value: "", label: "-" }, ...provinces.map((p) => ({ value: p.id, label: p.name }))]} onChange={(e) => setF({ ...f, province_id: e.target.value })} />
            <Field label="İlçe" value={f.district} onChange={(e) => setF({ ...f, district: e.target.value })} />
            <Field label="Adres" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} />
            <Field label="Telefon" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
            <Field label="Nöbet tarihi" type="date" value={f.on_duty_date} onChange={(e) => setF({ ...f, on_duty_date: e.target.value })} />
            <Field label="Nöbet saat" value={f.on_duty_hours} onChange={(e) => setF({ ...f, on_duty_hours: e.target.value })} />
            <button onClick={save} className="w-full px-4 py-2 bg-[#e11d2a] text-white rounded text-sm font-semibold">Ekle</button>
          </div>
        )}
      </div>
    </div>
  );
}
